// KnowledgeTab — Owner-only Admin-Sub-Tab „Wissen".
// Spiegelt Wissens-Chunks aus der Lab-Werkbank und weist pro Video EVE-Modi zu.
// Source-of-truth: mockups/13-knowledge/proposal.html (Doc-approved).
// Self-contained: lädt selbst via useAuth-Token, hält eigenen lokalen State.

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import {
  fetchKnowledgeVideos,
  syncKnowledge,
  setKnowledgeAssignment,
  type KnowledgeVideo,
  type KnowledgeSyncResult,
} from '@/lib/admin-knowledge-api';
import { EVE_MODE_OPTIONS } from './eve-modes';

export function KnowledgeTab() {
  const { accessToken } = useAuth();
  const [videos, setVideos] = useState<KnowledgeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncLine, setSyncLine] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await fetchKnowledgeVideos(accessToken);
      setVideos(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function runSync() {
    if (!accessToken || syncing) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const result: KnowledgeSyncResult = await syncKnowledge(accessToken);
      setSyncLine(formatSyncResult(result));
      await reload();
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  }

  const totalChunks = videos.reduce((sum, v) => sum + v.chunks, 0);

  return (
    <div className="space-y-5">
      {/* Sync-Leiste */}
      <section className="glass-strong rounded-2xl border border-white/5 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">Wissen</h2>
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-neutral-400 tabular">
                {totalChunks} Chunks · {videos.length} Videos
              </span>
            </div>
            <p className="mt-1 max-w-xl text-[12px] text-neutral-500">
              Wissens-Chunks aus der Lab-Werkbank — pro Video den EVE-Modi zuweisen.
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
            {syncLine ? (
              <span className="text-[12px] text-neutral-400">
                <span className="text-neutral-500">Zuletzt:</span> {syncLine}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void runSync()}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-500/50 bg-amber-500/20 px-4 py-2 text-[13px] font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:opacity-60"
            >
              <RefreshCw className={['size-4', syncing ? 'animate-spin' : ''].join(' ')} />
              {syncing ? 'Synchronisiere…' : 'Sync von Lab'}
            </button>
          </div>
        </div>

        {syncError ? (
          <div className="mt-3 rounded-full border border-rose-500/20 bg-rose-500/10 px-3 py-1 text-[11px] text-rose-300">
            {syncError}
          </div>
        ) : null}
      </section>

      {/* Video-Tabelle */}
      <section className="glass-strong overflow-hidden rounded-2xl border border-white/5">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <div>
            <h3 className="text-[13px] font-semibold">Videos &amp; Modus-Zuweisung</h3>
            <p className="text-[11px] text-neutral-500">
              Pro Video mehrere EVE-Modi wählbar — Klick togglet einen Modus.
            </p>
          </div>
          <span className="text-[11px] text-neutral-500">{videos.length} Videos</span>
        </div>

        {error ? (
          <div className="m-5 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="py-10 text-center text-[12px] text-neutral-500">Lade Wissen…</div>
        ) : videos.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-neutral-500">
            Noch keine Wissens-Videos. „Sync von Lab" spiegelt die Chunks aus der Werkbank.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-white/5 text-left text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="px-5 py-3 font-medium">Video</th>
                  <th className="px-4 py-3 font-medium">Chunks</th>
                  <th className="px-4 py-3 font-medium">Aktiv</th>
                  <th className="px-4 py-3 font-medium">EVE-Modus-Zuweisung</th>
                  <th className="px-5 py-3 text-right font-medium"> </th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <VideoRow
                    key={video.videoId}
                    video={video}
                    accessToken={accessToken}
                    onSaved={(agentKeys) =>
                      setVideos((prev) =>
                        prev.map((v) =>
                          v.videoId === video.videoId ? { ...v, agentKeys } : v,
                        ),
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-white/5 px-5 py-3 text-[11px] text-neutral-500">
          <span>
            Modi: <span className="text-amber-300/80">Amber gefüllt</span> = zugewiesen ·{' '}
            <span className="text-neutral-400">Outline</span> = nicht zugewiesen
          </span>
        </div>
      </section>
    </div>
  );
}

interface VideoRowProps {
  video: KnowledgeVideo;
  accessToken: string | null;
  onSaved: (agentKeys: string[]) => void;
}

function VideoRow({ video, accessToken, onSaved }: VideoRowProps) {
  const [selected, setSelected] = useState<string[]>(video.agentKeys);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  // Server-State änderte sich (z. B. nach Sync-Reload) → lokale Auswahl angleichen.
  useEffect(() => {
    setSelected(video.agentKeys);
  }, [video.agentKeys]);

  const dirty = !sameSet(selected, video.agentKeys);

  function toggle(key: string) {
    setRowError(null);
    setSavedFlash(false);
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  async function save() {
    if (!accessToken || saving) return;
    setSaving(true);
    setRowError(null);
    const next = selected;
    try {
      await setKnowledgeAssignment(accessToken, {
        videoId: video.videoId,
        agentKeys: next,
      });
      onSaved(next);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      setSelected(video.agentKeys);
      setRowError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  const active = `${video.active}/${video.chunks} aktiv`;
  const title = video.videoName ?? video.videoId;

  return (
    <tr className="border-t border-white/5 align-top hover:bg-white/[0.02]">
      <td className="px-5 py-4">
        <div className="font-medium text-neutral-100">{title}</div>
        <div className="font-mono text-[10px] text-neutral-500">{video.videoId}</div>
        {selected.length === 0 ? (
          <div className="mt-1.5 text-[11px] text-neutral-500">⚠ Keinem Modus zugewiesen</div>
        ) : null}
      </td>
      <td className="px-4 py-4 text-[12px] text-neutral-400 tabular whitespace-nowrap">
        {video.chunks} Abschnitte
      </td>
      <td className="px-4 py-4 text-[12px] whitespace-nowrap">
        <span className={video.active < video.chunks ? 'text-neutral-300 tabular' : 'text-emerald-300 tabular'}>
          {active}
        </span>
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap gap-1.5">
          {EVE_MODE_OPTIONS.map((mode) => {
            const on = selected.includes(mode.key);
            return (
              <button
                key={mode.key}
                type="button"
                onClick={() => toggle(mode.key)}
                aria-pressed={on}
                className={[
                  'rounded-full border px-2.5 py-1 text-[11px] font-medium transition',
                  on
                    ? 'border-amber-500/50 bg-amber-500/20 text-amber-200'
                    : 'border-white/10 bg-transparent text-neutral-400 hover:border-white/25 hover:text-neutral-200',
                ].join(' ')}
              >
                {on ? <span className="mr-1 text-[10px]">✓</span> : null}
                {mode.label}
              </button>
            );
          })}
        </div>
        {rowError ? (
          <div className="mt-1.5 text-[11px] text-rose-300">{rowError}</div>
        ) : null}
      </td>
      <td className="px-5 py-4 text-right whitespace-nowrap">
        {dirty ? (
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        ) : savedFlash ? (
          <span className="text-[11px] text-emerald-400/80">✓ gespeichert</span>
        ) : null}
      </td>
    </tr>
  );
}

function formatSyncResult(result: KnowledgeSyncResult): string {
  if (result.skipped) {
    return result.message ?? 'Unverändert — nichts zu spiegeln.';
  }
  const base = `${result.upserted} gespiegelt, ${result.deleted} entfernt`;
  return result.message ? `${base} — ${result.message}` : base;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((k) => setB.has(k));
}
